import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Mail, CheckCircle, Loader2, RefreshCw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const EmailVerificationPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, refreshUser } = useAuth();
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [pageEmail, setPageEmail] = useState('');
  const inputRefs = useRef([]);
  
  const selectedPlan = location.state?.plan;
  const billingCycle = location.state?.billingCycle;

  // Email'i çeşitli kaynaklardan al
  useEffect(() => {
    const emailFromState = location.state?.email;
    const emailFromUser = user?.email;
    const emailFromStorage = localStorage.getItem('pending_verification_email');
    
    const resolvedEmail = emailFromState || emailFromUser || emailFromStorage;
    
    if (resolvedEmail) {
      setPageEmail(resolvedEmail);
      // localStorage'a kaydet (sayfa yenilenirse kaybolmasın)
      localStorage.setItem('pending_verification_email', resolvedEmail);
    }
  }, [location.state?.email, user?.email]);

  // Artık email yerine pageEmail kullanılacak
  const email = pageEmail;

  useEffect(() => {
    // If already verified, redirect
    if (user?.email_verified) {
      navigate('/signals');
    }
  }, [user, navigate]);

  useEffect(() => {
    // Countdown for resend button
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleCodeChange = (index, value) => {
    // Only allow numbers
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all digits entered
    if (index === 5 && value) {
      const fullCode = newCode.join('');
      if (fullCode.length === 6) {
        handleVerify(fullCode);
      }
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData.length === 6) {
      const newCode = pastedData.split('');
      setCode(newCode);
      handleVerify(pastedData);
    }
  };

  const handleVerify = async (verificationCode) => {
    if (!email) {
      toast.error('E-posta adresi bulunamadı');
      return;
    }

    setLoading(true);
    
    try {
      const response = await axios.post('/auth/verify-email', {
        email,
        code: verificationCode
      });
      
      if (response.data.verified) {
        toast.success('E-posta başarıyla doğrulandı!');
        
        // Temizle - artık gerekli değil
        localStorage.removeItem('pending_verification_email');
        
        // Refresh user data
        if (refreshUser) {
          await refreshUser();
        }
        
        // Pro plan seçildiyse ödeme sayfasına yönlendir
        setTimeout(() => {
          if (selectedPlan === 'pro') {
            navigate(`/payment?plan=pro&billing=${billingCycle || 'yearly'}`);
          } else {
            navigate('/signals');
          }
        }, 1000);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Doğrulama başarısız');
      // Clear code on error
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!email || countdown > 0) return;

    setResending(true);
    
    try {
      await axios.post('/auth/resend-verification', { email });
      toast.success('Doğrulama kodu tekrar gönderildi');
      setCountdown(60); // 60 second cooldown
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kod gönderilemedi');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-dark flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <Card className="border-border/40 shadow-xl">
          <CardHeader className="text-center">
            <div className="mx-auto bg-primary/20 text-primary p-4 rounded-full w-fit mb-4">
              <Mail className="h-8 w-8" />
            </div>
            <CardTitle className="text-2xl font-heading">E-posta Doğrulama</CardTitle>
            <CardDescription className="mt-2">
              {email ? (
                <>
                  <span className="text-primary font-medium">{email}</span> adresine 6 haneli doğrulama kodu gönderdik.
                  {selectedPlan === 'pro' && (
                    <p className="mt-2 text-amber-400 text-sm">
                      🎯 Pro {billingCycle === 'yearly' ? 'Yıllık' : 'Aylık'} plan seçtiniz. Doğrulama sonrası ödeme sayfasına yönlendirileceksiniz.
                    </p>
                  )}
                </>
              ) : (
                <div className="text-amber-400">
                  <p>E-posta adresi bulunamadı.</p>
                  <p className="mt-2 text-sm text-slate-400">
                    Lütfen <a href="/login" className="text-primary underline">giriş yapın</a> ve tekrar deneyin.
                  </p>
                </div>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {email ? (
            <div className="space-y-6">
              {/* Code Input */}
              <div className="flex justify-center gap-2" onPaste={handlePaste}>
                {code.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (inputRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleCodeChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    className="w-12 h-14 text-center text-2xl font-bold bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all"
                    disabled={loading}
                  />
                ))}
              </div>

              <p className="text-center text-sm text-slate-500">
                ⏱️ Kod 15 dakika geçerlidir
              </p>

              {/* Verify Button */}
              <Button
                onClick={() => handleVerify(code.join(''))}
                className="w-full btn-primary"
                disabled={loading || code.join('').length !== 6}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Doğrulanıyor...
                  </>
                ) : (
                  <>
                    <CheckCircle className="mr-2 h-4 w-4" />
                    Doğrula
                  </>
                )}
              </Button>

              {/* Resend Button */}
              <div className="text-center">
                <button
                  onClick={handleResend}
                  disabled={resending || countdown > 0}
                  className="text-sm text-slate-400 hover:text-primary transition-colors inline-flex items-center gap-2 disabled:opacity-50"
                >
                  <RefreshCw className={`h-4 w-4 ${resending ? 'animate-spin' : ''}`} />
                  {countdown > 0 ? (
                    `Tekrar gönder (${countdown}s)`
                  ) : resending ? (
                    'Gönderiliyor...'
                  ) : (
                    'Kodu tekrar gönder'
                  )}
                </button>
              </div>

              {/* Skip for now - Limited access */}
              <div className="pt-4 border-t border-white/10">
                <p className="text-xs text-slate-500 text-center mb-3">
                  Şimdilik atlayabilirsiniz, ancak bazı özellikler kısıtlı olacaktır.
                </p>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/signals')}
                  className="w-full text-slate-400 hover:text-white"
                >
                  Şimdilik Atla
                </Button>
              </div>
            </div>
            ) : (
              <div className="text-center py-4">
                <Button
                  onClick={() => navigate('/login')}
                  className="btn-primary"
                >
                  Giriş Yap
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EmailVerificationPage;
